import React from 'react';
import PropTypes from 'prop-types';
import { PSPMarkdownContent, SectionHeader } from 'components/molecules';
import { Container } from 'components/atoms/Page';
import checkMarkIcon from 'assets/images/icons/size-guide/checkmarkicon.svg';
import { H4, H2, H3 } from 'components/atoms/Typography/Typography';
import './SizeChartSection.scss';
export default function SizeChartSection({
	title,
	description,
	slug,
	headers,
	rows,
	columnWidths,
	tableType,
	pageType = 'PSP',
	suffix,
	image,
	isFullWidth,
}) {
	// Generate grid template based on number of columns and optional width ratios
	const getGridTemplate = () => {
		if (columnWidths && columnWidths.length === headers.length) {
			// Use provided column widths (e.g., [1, 1, 1, 1.5] or ['1fr', '1fr', '1fr', 'auto'])
			return columnWidths
				.map((width) =>
					typeof width === 'number' ? `${width}fr` : width
				)
				.join(' ');
		}
		// Default: evenly distributed columns
		return `repeat(${headers.length}, 1fr)`;
	};

	const gridTemplate = getGridTemplate();

	const renderCellContent = (cell) => {
		if (typeof cell === 'object') {
			switch (cell.type) {
				case 'icon': {
					const iconWidth = cell.iconWidth || 64;
					const iconHeight = cell.iconHeight || 64;
					return (
						<div className={`psp-table-cell-icon ${slug}`}>
							{cell.icon && (
								<img
									src={cell.icon}
									alt="icon"
									width={iconWidth}
									height={iconHeight}
								/>
							)}
							<div className="psp-table-cell-icon-description">
								<H4
									weight="ultraBold"
									color="black"
								>
									{cell.heading}
								</H4>
								<PSPMarkdownContent
									content={cell.description}
									additionalProps={{
										paragraph: {
											color: 'black',
											weight: 'standard',
										},
										anchor: {
											color: 'black',
											weight: 'light',
											size: 'small',
										},
									}}
								/>
							</div>
						</div>
					);
				}
				case 'checklist':
					return (
						<ul className="psp-table-cell-checklist">
							{cell.list.map((item, index) => (
								<li
									key={index}
									className="psp-check-list"
								>
									<img
										src={checkMarkIcon}
										alt="psp-check-mark"
										width="16"
										height="16"
									/>
									{item}
								</li>
							))}
						</ul>
					);
				case 'checkbox':
					return (
						<div className="psp-table-cell-checkbox">
							<label className="psp-checkbox-label">
								<input
									type="checkbox"
									defaultChecked={!!cell.checked}
									aria-label={
										cell.label || cell.text || 'checkbox'
									}
									className="psp-checkbox-input"
								/>
								<span
									className="psp-checkbox-box"
									aria-hidden="true"
								/>
								<span className="psp-checkbox-label-text">
									{cell.label || cell.text}
								</span>
							</label>
						</div>
					);
				case 'markdown':
					return (
						<div className="psp-table-cell-markdown">
							<PSPMarkdownContent
								content={cell.markdown}
								additionalProps={{
									paragraph: {
										color: 'black',
										weight: 'standard',
									},
									anchor: {
										color: 'black',
										weight: 'light',
										size: 'small',
									},
								}}
							/>
						</div>
					);
				default:
					return cell;
			}
		}
		return cell;
	};
	return (
		<section className="psp-size-chart">
			<Container>
				<div
					className="title-head"
					style={{ textAlign: pageType == 'SGP' ? 'center' : 'left' }}
				>
					{pageType !== 'SGP' && (
						<>
							<H2
								weight="ultraBold"
								color="black"
								className="head-title"
								size="large"
								family="rounded"
							>
								{title}
							</H2>
							<PSPMarkdownContent
								content={description}
								additionalProps={{
									paragraph: {
										color: 'black',
										weight: 'standard',
									},
									anchor: {
										color: 'black',
										weight: 'light',
										size: 'small',
									},
								}}
							/>
						</>
					)}
					{pageType === 'SGP' && (
						<SectionHeader
							title={title}
							titleProps={{
								className: 'card-block-section__header-title',
							}}
							image={image}
							suffix={suffix}
							contentAlign="text-center"
							description={description}
							additionalProps={{
								paragraph: {
									color: 'black',
									weight: 'standard',
								},
								anchor: {
									color: 'black',
									weight: 'light',
									size: 'small',
								},
							}}
							className={isFullWidth ? 'full-width-table' : ''}
						/>
					)}
				</div>
				<table
					className={`custom-table ${slug} ${pageType.toLowerCase()}-table ${tableType}`}
					style={{ '--grid-template': gridTemplate }}
				>
					<thead>
						<tr>
							{headers.map((header, index) => (
								<th key={index}>
									<H3
										weight="ultraBold"
										color="black"
										size="small"
										family="rounded"
									>
										{header.label}
									</H3>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr key={rowIndex}>
								{headers.map((header, cellIndex) => (
									<td key={cellIndex}>
										{renderCellContent(row[header.key])}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Container>
		</section>
	);
}

SizeChartSection.propTypes = {
	slug: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	image: PropTypes.string,
	suffix: PropTypes.string,
	description: PropTypes.string.isRequired,
	tableType: PropTypes.string,
	pageType: PropTypes.string,
	headers: PropTypes.arrayOf(PropTypes.string).isRequired,
	rows: PropTypes.arrayOf(
		PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.shape({
					type: PropTypes.oneOf([
						'icon',
						'checklist',
						'checkbox',
						'unorderedList',
						'text',
						'markdown',
					]),
					icon: PropTypes.string,
					heading: PropTypes.string,
					list: PropTypes.arrayOf(PropTypes.string),
					markdown: PropTypes.string,
					label: PropTypes.string,
					checked: PropTypes.bool,
					text: PropTypes.string,
				}),
			])
		)
	).isRequired,
	// Optional: Array of column widths (numbers for 'fr' units or strings like 'auto', 'min-content')
	// Length must match headers.length. Example: [1, 1, 1, 1.5] or ['1fr', '1fr', '1fr', 'auto']
	columnWidths: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.number, PropTypes.string])
	),
};

